import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LIFECYCLE_LABELS, LIFECYCLE_TRANSITIONS } from '@hospasense/contracts';
import type { LifecycleState, Organisation } from '@hospasense/contracts';
import { ApiError } from '../lib/api.js';
import {
  useArchiveOrganisation,
  useInstallations,
  useLifecycleEvents,
  useOrganisation,
  useTransitionLifecycle,
} from '../lib/queries.js';
import { useAuth } from '../lib/auth.js';
import { formatDateTime, relativeTime, titleCase } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { ChecklistTab } from './workspace/ChecklistTab.js';
import { NotesTab } from './workspace/NotesTab.js';
import { TasksTab } from './workspace/TasksTab.js';
import { TimelineTab } from './workspace/TimelineTab.js';
import { BillingTab } from './workspace/BillingTab.js';
import { TesterAccessCard } from './TesterAccessCard.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  HealthPill,
  LifecyclePill,
  LoadingState,
  Modal,
  PageHeader,
  PermissionDeniedState,
  Select,
  Textarea,
  useToast,
} from '../components/ui/index.js';

export function OrganisationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const overview = tab === 'overview';
  const query = useOrganisation(id);
  const history = useLifecycleEvents(overview ? id : undefined);
  const installations = useInstallations(id, hasPermission('installation.read') && overview);

  if (!hasPermission('organisation.read')) {
    return <PermissionDeniedState what="organisation details" />;
  }
  if (query.isLoading) return <LoadingState label="Loading organisation…" />;
  if (query.isError || !query.data) {
    const denied = query.error instanceof ApiError && query.error.status === 403;
    if (denied) return <PermissionDeniedState what="this organisation" />;
    return (
      <ErrorState
        description="This organisation could not be loaded — it may not exist."
        onRetry={() => void query.refetch()}
      />
    );
  }
  const org = query.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title={org.displayName}
        subtitle={`${org.legalName} · ${titleCase(org.organisationType)} · ${org.jurisdiction}`}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: 'Control', to: '/' },
              { label: 'Organisations', to: '/organisations' },
              { label: org.displayName },
            ]}
          />
        }
        actions={<DetailActions org={org} />}
      />

      {org.archivedAt && (
        <p className="rounded-lg bg-warning-bg px-3 py-2 text-sm text-warning" role="status">
          Archived {formatDateTime(org.archivedAt)}. Archived organisations are read-only.
        </p>
      )}

      <WorkspaceTabBar
        active={tab}
        onSelect={(next) => {
          const p = new URLSearchParams(params);
          if (next === 'overview') p.delete('tab');
          else p.set('tab', next);
          setParams(p, { replace: true });
        }}
      />

      {tab === 'onboarding' && <ChecklistTab orgId={org.id} />}
      {tab === 'billing' && <BillingTab orgId={org.id} />}
      {tab === 'notes' && <NotesTab orgId={org.id} />}
      {tab === 'tasks' && <TasksTab orgId={org.id} />}
      {tab === 'timeline' && <TimelineTab orgId={org.id} />}

      {overview && (
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardHeader title="Summary" />
            <CardBody>
              <dl className="space-y-2 text-sm">
                <Row label="Lifecycle">
                  <LifecyclePill state={org.lifecycleState} />
                </Row>
                <Row label="Deployment">
                  {org.deploymentType === 'on_prem' ? 'On-prem' : 'Cloud'}
                </Row>
                <Row label="Type">{titleCase(org.organisationType)}</Row>
                <Row label="Jurisdiction">
                  <span className="font-mono text-xs">{org.jurisdiction}</span>
                </Row>
                <Row label="Tags">
                  {org.tags.length === 0 ? (
                    '—'
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {org.tags.map((t) => (
                        <Badge key={t} tone="accent">
                          {t}
                        </Badge>
                      ))}
                    </span>
                  )}
                </Row>
                <Row label="Owners">
                  {org.owners.length === 0 ? (
                    <span className="text-warning">Unowned</span>
                  ) : (
                    org.owners.map((o) => o.displayName).join(', ')
                  )}
                </Row>
                <Row label="Version">
                  <span className="tabular-nums">{org.version}</span>
                </Row>
                <Row label="Updated">
                  <span title={formatDateTime(org.updatedAt)}>{relativeTime(org.updatedAt)}</span>
                </Row>
                <Row label="Created">{formatDateTime(org.createdAt)}</Row>
              </dl>
            </CardBody>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader title="Contacts" subtitle="Business contacts (CONFIDENTIAL class)" />
            <CardBody>
              {org.contacts.length === 0 ? (
                <p className="text-sm text-warning">
                  No contacts recorded — this organisation appears under “Needs attention”.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {org.contacts.map((c) => (
                    <li
                      key={c.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                    >
                      <div>
                        <p className="font-medium">
                          {c.fullName}
                          {c.isPrimary && (
                            <span className="ml-2">
                              <Badge tone="info">Primary</Badge>
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-text-muted">{c.roleTitle}</p>
                      </div>
                      <div className="text-right text-xs text-text-secondary">
                        <p>{c.email}</p>
                        {c.phone && <p className="tabular-nums">{c.phone}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {overview &&
        !org.archivedAt &&
        org.lifecycleState !== 'live' &&
        hasPermission('provisioning.dispatch') && <TesterAccessCard orgId={org.id} />}

      {overview && hasPermission('installation.read') && (
        <Card>
          <CardHeader
            title="Installations"
            subtitle="Summary records from signed heartbeats — full fleet telemetry is a later stage"
          />
          <CardBody>
            {installations.isLoading ? (
              <LoadingState />
            ) : installations.isError ? (
              <ErrorState
                description="Installation summaries could not be loaded."
                onRetry={() => void installations.refetch()}
              />
            ) : (installations.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-text-muted">No installations registered.</p>
            ) : (
              <ul className="divide-y divide-border">
                {installations.data!.items.map((inst) => (
                  <li
                    key={inst.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                  >
                    <span className="font-mono text-xs">{inst.installationKey}</span>
                    <span className="flex items-center gap-3">
                      <span className="text-xs text-text-muted">v{inst.appVersion ?? '—'}</span>
                      <HealthPill health={inst.health} />
                      <span
                        className="text-xs text-text-muted"
                        title={formatDateTime(inst.lastSeenAt)}
                      >
                        seen {relativeTime(inst.lastSeenAt)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}

      {overview && (
        <Card>
          <CardHeader
            title="Lifecycle history"
            subtitle="Immutable — recorded at every transition"
          />
          <CardBody>
            {history.isLoading ? (
              <LoadingState />
            ) : history.isError ? (
              <ErrorState
                description="Lifecycle history could not be loaded."
                onRetry={() => void history.refetch()}
              />
            ) : (
              <ol className="space-y-0" data-testid="lifecycle-history">
                {history.data!.items.map((e, i) => (
                  <li key={e.id} className="relative flex gap-3 pb-4 text-sm">
                    <span className="flex flex-col items-center" aria-hidden="true">
                      <span className="mt-1 size-2 rounded-full bg-accent-fg" />
                      {i < history.data!.items.length - 1 && (
                        <span className="w-px flex-1 bg-border" />
                      )}
                    </span>
                    <div className="min-w-0 pb-1">
                      <p className="font-medium">
                        {e.fromState ? `${LIFECYCLE_LABELS[e.fromState]} → ` : ''}
                        {LIFECYCLE_LABELS[e.toState]}
                      </p>
                      <p className="text-xs text-text-secondary">{e.reason}</p>
                      <p className="text-xs text-text-muted">
                        {e.actorDisplayName ?? 'System'} · {formatDateTime(e.occurredAt)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

const WORKSPACE_TABS = [
  { key: 'overview', label: 'Overview', permission: null },
  { key: 'onboarding', label: 'Onboarding', permission: 'onboarding.read' },
  { key: 'billing', label: 'Billing & Contracts', permission: null },
  { key: 'notes', label: 'Notes', permission: 'note.read' },
  { key: 'tasks', label: 'Tasks', permission: 'task.read' },
  { key: 'timeline', label: 'Timeline', permission: null },
] as const;

function WorkspaceTabBar({
  active,
  onSelect,
}: {
  active: string;
  onSelect: (tab: string) => void;
}) {
  const { hasPermission } = useAuth();
  const tabs = WORKSPACE_TABS.filter((t) => t.permission === null || hasPermission(t.permission));
  return (
    <div
      role="tablist"
      aria-label="Workspace sections"
      className="flex flex-wrap gap-1 border-b border-border"
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          role="tab"
          type="button"
          aria-selected={active === t.key}
          onClick={() => onSelect(t.key)}
          className={[
            'rounded-t-lg px-3 py-2 text-sm transition-colors duration-150',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            active === t.key
              ? 'border-b-2 border-accent-fg font-medium text-text-primary'
              : 'text-text-secondary hover:bg-bg-subtle',
          ].join(' ')}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-xs text-text-muted">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}

function DetailActions({ org }: { org: Organisation }) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [toState, setToState] = useState<LifecycleState | ''>('');
  const [reason, setReason] = useState('');
  const transition = useTransitionLifecycle(org.id);
  const archive = useArchiveOrganisation(org.id);

  if (org.archivedAt) return null;
  const targets = LIFECYCLE_TRANSITIONS[org.lifecycleState];

  const doTransition = async (): Promise<void> => {
    if (!toState) return;
    try {
      await transition.mutateAsync({ toState, reason, expectedVersion: org.version });
      toast.success(`Lifecycle changed to ${LIFECYCLE_LABELS[toState]}`);
      setLifecycleOpen(false);
      setReason('');
      setToState('');
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.code === 'VERSION_CONFLICT'
          ? 'Someone else changed this organisation first — reload and retry.'
          : err instanceof ApiError
            ? err.message
            : 'Transition failed',
      );
    }
  };

  const doArchive = async (): Promise<void> => {
    try {
      await archive.mutateAsync({ reason, expectedVersion: org.version });
      toast.success('Organisation archived');
      setArchiveOpen(false);
      navigate('/organisations');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Archive failed');
    }
  };

  return (
    <>
      {hasPermission('organisation.update') && (
        <Button variant="outline" onClick={() => navigate(`/organisations/${org.id}/edit`)}>
          Edit
        </Button>
      )}
      {hasPermission('organisation.lifecycle.transition') && targets.length > 0 && (
        <Button onClick={() => setLifecycleOpen(true)}>Change lifecycle</Button>
      )}
      {hasPermission('organisation.archive') && (
        <Button variant="danger" onClick={() => setArchiveOpen(true)}>
          Archive
        </Button>
      )}

      <Modal
        open={lifecycleOpen}
        onClose={() => setLifecycleOpen(false)}
        title="Change lifecycle state"
        footer={
          <>
            <Button variant="outline" onClick={() => setLifecycleOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void doTransition()}
              loading={transition.isPending}
              disabled={!toState || reason.trim().length < 5}
            >
              Apply transition
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Current state: <LifecyclePill state={org.lifecycleState} />. Only transitions allowed by
            the state machine are offered; every change is recorded immutably.
          </p>
          <Select
            label="New state"
            required
            value={toState}
            onChange={(e) => setToState(e.target.value as LifecycleState)}
          >
            <option value="">Choose a state…</option>
            {targets.map((t) => (
              <option key={t} value={t}>
                {LIFECYCLE_LABELS[t]}
              </option>
            ))}
          </Select>
          <Textarea
            label="Reason"
            required
            minLength={5}
            hint="Recorded in the immutable history and the audit log."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        onConfirm={() => void doArchive()}
        title={`Archive ${org.displayName}?`}
        destructive
        confirmLabel="Archive"
        loading={archive.isPending}
        body={
          <div className="space-y-3">
            <p>
              Archiving hides the organisation from active lists. Nothing is deleted and the full
              history remains readable.
            </p>
            <Textarea
              label="Reason"
              required
              minLength={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        }
      />
    </>
  );
}
